import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import { duplicateProjectFestival } from '@/domain/project-festival/actions/duplicate-project-festival.action';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

export function ModalDuplicateProject({
  open,
  onClose,
  originalProject,
}: {
  open: boolean;
  onClose: () => void;
  originalProject: ProjectFestival;
}) {
  const dispatch = useAppDispatch();

  const onSubmit = (data: DuplicateProjectFormData) => {
    executeDuplicateProject(data);
  };

  const executeDuplicateProject = async (data: DuplicateProjectFormData) => {
    const params = {
      originalProjectId: originalProject.id,
      newProjectName: data.newName,
      copyImpactCo2: data.copyImpactCo2,
      copyImpactEnv: data.copyImpactEnv,
      copyImpactEss: data.copyImpactEss,
    };
    try {
      await dispatch(duplicateProjectFestival(params)).unwrap();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FSModal open={open} onClose={onClose}>
      <Stack spacing={3}>
        <FSModalTitle
          title={translate('DuplicateProject.duplicate-project', {
            name: originalProject.name,
          })}
        />
        <DuplicateProjectForm
          onSubmit={onSubmit}
          onCancel={onClose}
          originalProjectName={originalProject.name}
        />
      </Stack>
    </FSModal>
  );
}

type DuplicateProjectFormData = {
  newName: string;
  copyImpactCo2: boolean;
  copyImpactEnv: boolean;
  copyImpactEss: boolean;
};

function DuplicateProjectForm({
  onCancel,
  onSubmit,
  originalProjectName,
}: {
  onCancel: () => void;
  onSubmit: (data: DuplicateProjectFormData) => void;
  originalProjectName: string;
}) {
  const defaultValues: DuplicateProjectFormData = {
    newName: translate('DuplicateProject.copy-of', {
      name: originalProjectName,
    }),
    copyImpactCo2: true,
    copyImpactEnv: true,
    copyImpactEss: true,
  };

  const { handleSubmit, register, watch, formState } =
    useForm<DuplicateProjectFormData>({
      defaultValues,
    });

  const { errors } = formState;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        <FSTextField
          label={translate('DuplicateProject.Form.new-project-name')}
          register={{
            ...register('newName', {
              required: translate('Common.field-is-required'),
            }),
          }}
          error={!!errors.newName}
          helperText={errors.newName?.message}
        />
        <Stack spacing={1}>
          <Typography sx={{ fontSize: '1rem', fontWeight: '800' }}>
            {translate('DuplicateProject.Form.select-data-to-copy')}
          </Typography>
          <Stack sx={{ paddingLeft: 1 }}>
            <FormControlLabel
              label={translate(
                'DuplicateProject.Form.choices.general-informations',
              )}
              control={<Checkbox checked={true} disabled />}
            />
            <FormControlLabel
              label={translate('DuplicateProject.Form.choices.co2')}
              control={
                <Checkbox
                  {...register('copyImpactCo2')}
                  checked={watch('copyImpactCo2')}
                />
              }
            />
            <FormControlLabel
              label={translate('DuplicateProject.Form.choices.env')}
              control={
                <Checkbox
                  {...register('copyImpactEnv')}
                  checked={watch('copyImpactEnv')}
                />
              }
            />
            <FormControlLabel
              label={translate('DuplicateProject.Form.choices.ess')}
              control={
                <Checkbox
                  {...register('copyImpactEss')}
                  checked={watch('copyImpactEss')}
                />
              }
            />
          </Stack>
        </Stack>
      </Stack>
      <Stack direction={'row'} justifyContent={'flex-end'} sx={{ mt: 2 }}>
        <FSSecondaryButton onClick={onCancel}>
          {translate('Form.cancel')}
        </FSSecondaryButton>
        <FSPrimaryButton type="submit">
          {translate('DuplicateProject.Form.duplicate')}
        </FSPrimaryButton>
      </Stack>
    </form>
  );
}
